/**
 * HELP-GUIDE-PROVIDER
 * Reads the guide of the page (written in Strapi) and opens it : from the link of the header (top of the guide)
 * or from the hint of a zone (on its section)
 */
import { ReactNode, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import i18n from "@/translate/i18n"
import { HelpGuideContext, HelpGuideContextValue } from "@/components/help-guide.context"
import HelpArticleHelp from "@/help/help-article.help"
import { filterHelpSectionsByMode, isHelpArticleVisible } from "@/utils/help-article.utils"
import { useHelpArticle } from "@/utils/use-help-article.utils"
import { useAppSelector } from "@/core/root-reducer"

interface OwnProps {
  helpKey?: string //Key of the guide of the page
  userRole: string | null
  isGollac?: boolean //Display of the survey : the sections of the other display are hidden
  children?: ReactNode
}

function HelpGuideProvider(props: OwnProps) {
  const session = useAppSelector((state) => state._session)
  const article = filterHelpSectionsByMode(
    useHelpArticle(props.helpKey),
    !!props.isGollac,
    !!session.accountOptions.showAccountCompareForObserver
  )

  //Window open : null if closed, the section to show if there is one
  const [opened, setOpened] = useState<{ section?: string } | null>(null)
  const location = useLocation()
  const navigate = useNavigate()

  const isAvailable = i18n.language === "fr" && !!article && isHelpArticleVisible(article.audience, props.userRole)

  //Coming from a link of another guide : the guide of this page opens once it is available
  useEffect(() => {
    if (!(location.state as any)?.openGuide || !isAvailable) return
    setOpened({})
    navigate(location.pathname + location.search, { replace: true, state: null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, isAvailable])

  const value = useMemo<HelpGuideContextValue>(
    () => ({ article, isAvailable, open: (section) => setOpened({ section }), isGollac: !!props.isGollac }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [article?.key, article?.sections, isAvailable, props.isGollac]
  )

  return (
    <HelpGuideContext.Provider value={value}>
      {props.children}

      {opened && props.helpKey && isAvailable && (
        <HelpArticleHelp
          helpKey={props.helpKey}
          section={opened.section}
          isGollac={!!props.isGollac}
          onClose={() => setOpened(null)}
        />
      )}
    </HelpGuideContext.Provider>
  )
}

export default HelpGuideProvider
